import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

// Quill Dependency
import Quill from 'quill';
import { FontStyle } from 'quill/formats/font';
import { SizeStyle } from 'quill/formats/size';
import 'quill/dist/quill.snow.css';

// Quill custom toolbar
const fontFamilyArr = [
  'helvetica',
  'sans-serif',
  'serif',
  'monospace',
  'var(--m-plus-1p)',
  'Dela Gothic One',
  'Rampart One',
  'Shippori Mincho B1',
  'Train One',
  'Darumadrop One',
  'Noto Serif JP',
];

const fontSizeArr = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '40px', '48px', '52px', '56px', '60px', '68px'];

// Register font and size formats
const Font = FontStyle;
Font.whitelist = fontFamilyArr;
Quill.register(Font, true);

const Size = SizeStyle;
Size.whitelist = fontSizeArr;
Quill.register(Size, true);

const colorArr = [
  '#000000',
  '#e60000',
  '#ff9900',
  '#ffff00',
  '#008a00',
  '#0066cc',
  '#9933ff',
  '#ffffff',
  '#facccc',
  '#ffebcc',
  '#ffffcc',
  '#cce8cc',
  '#cce0f5',
  '#ebd6ff',
  '#bbbbbb',
  '#f06666',
  '#ffc266',
  '#ffff66',
  '#66b966',
  '#66a3e0',
  '#c285ff',
  '#888888',
  '#a10000',
  '#b26b00',
  '#b2b200',
  '#006100',
  '#0047b2',
  '#6b24b2',
  '#444444',
  '#5c0000',
  '#663d00',
  '#666600',
  '#003700',
  '#002966',
  '#3d1466',
  'transparent',
];

const headerArr = [false, '4', '3', '2', '1'];

function selectAllTextHandler(this: any) {
  if (this.quill) this.quill.setSelection(0, this.quill.getLength());
}

const modules: any = {
  toolbar: {
    container: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ size: fontSizeArr }],
      [{ header: headerArr }],
      [{ color: colorArr.slice(0, -1) }, { background: colorArr }],
      [{ font: fontFamilyArr }],
      [{ align: [] }],
      ['link'],
      [{ selectAllText: 'Select all' }],
    ],
    handlers: {
      selectAllText: selectAllTextHandler,
    },
  },
};

// Types
interface EditorProps {
  readOnly?: boolean;
  defaultValue?: any;
  onTextChange?: (text: string) => void;
  onSelectionChange?: (...args: any[]) => void;
}

const CustomQuill = forwardRef<Quill | null, EditorProps>(
  ({
    readOnly,
    defaultValue,
    onTextChange,
    onSelectionChange,
  }, ref) => {
    const containerRef = useRef(null);
    const defaultValueRef = useRef(defaultValue);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange;
      onSelectionChangeRef.current = onSelectionChange;
      defaultValueRef.current = defaultValue;
    });

    useEffect(() => {
      if (ref && typeof ref !== 'function' && ref.current) ref.current.enable(!readOnly);
    }, [ref, readOnly]);

    useEffect(() => {
      const container = containerRef.current;
      const editorContainer = container.appendChild(container.ownerDocument.createElement('div'));
      const quill = new Quill(editorContainer, {
        theme: 'snow',
        modules,
      });

      // PDEV3-34: prevent browser auto-translation from rewriting the editor
      // content (.ql-editor), which would otherwise be saved/published translated.
      // Both signals for cross-browser coverage (Chrome + Edge).
      quill.root.setAttribute('translate', 'no');
      quill.root.classList.add('notranslate');

      if (ref && typeof ref !== 'function') ref.current = quill;

      // Preserve multiple spaces when loading content
      const Delta = Quill.import('delta');
      quill.clipboard.addMatcher(Node.TEXT_NODE, (node: any) => {
        let text = '';
        if (node.nodeType === Node.TEXT_NODE) {
          text = node.data || '';
        }
        // Return new Delta with preserved non-breaking spaces
        return new Delta().insert(text);
      });

      if (defaultValueRef.current) {
        let html = defaultValueRef.current;
        // Add an extra line break at the end to compensate for the one Quill removes
        // Ref: https://github.com/slab/quill/issues/2075
        if (/<p><br\s*\/?><\/p>$/.test(html)) {
          html += '<p><br></p>';
        }
        quill.clipboard.dangerouslyPasteHTML(html);
      }

      quill.on(Quill.events.TEXT_CHANGE, () => onTextChangeRef.current?.(quill.root.innerHTML));
      quill.on(Quill.events.SELECTION_CHANGE, (...args) => onSelectionChangeRef.current?.(...args));

      return () => {
        if (ref && typeof ref !== 'function') ref.current = null;
        container.innerHTML = '';
      };
    }, [ref]);

    return <div ref={containerRef} />;
  },
);

CustomQuill.displayName = 'CustomQuill';

export default CustomQuill;
